import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/esm/ArrowBack';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import PersonnelFolderCard from '../../components/field/PersonnelFolderCard';
import teamService, { TeamListItem } from '../../services/api/teamService';
import tasksService, { Task } from '../../services/api/tasksService';
import timesheetService, { Timesheet, Worker } from '../../services/api/timesheetService';
import attendanceService, { AttendanceRecord } from '../../services/api/attendanceService';
import { RootState } from '../../store';

type WorkerStatus = 'clocked_in' | 'clocked_out' | 'not_seen';

interface WorkerActivity {
  worker: Worker;
  team?: TeamListItem;
  attendance?: AttendanceRecord;
  timesheets: Timesheet[];
  currentTasks: Task[];
  status: WorkerStatus;
  hoursToday: number;
}

interface CrewSummary {
  id: string;
  team: TeamListItem | null;
  workers: WorkerActivity[];
}

const today = () => new Date().toISOString().slice(0, 10);

const workerLoginSlug = (worker: Worker) => {
  const slug = worker.name
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '.')
    .replace(/(^\.|\.$)/g, '')
    .toLowerCase();
  return `${slug || 'field.worker'}@fairmont.com`;
};

const loginForWorker = (worker: Worker) => {
  return {
    email: workerLoginSlug(worker),
  };
};

const statusLabel: Record<WorkerStatus, string> = {
  clocked_in: 'Clocked in',
  clocked_out: 'Clocked out',
  not_seen: 'Not seen today',
};

const isConstructionCrew = (team?: TeamListItem | null) =>
  team?.teamType === 'construction' || /construction|crew|formwork|block|steel|masonry|plumb|electrical|roof|paint/i.test(team?.name ?? '');

export default function AdminWorkerMonitorPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeProjectId, projects, loading: projectsLoading } = useSelector((state: RootState) => state.projects);
  const effectiveProjectId = activeProjectId ?? projects[0]?.id ?? null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const selectedCrewId = searchParams.get('crew');

  const load = async () => {
    if (!effectiveProjectId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const date = today();
      const [teamRows, workerRows, taskRows, timesheetRows, attendanceRows] = await Promise.all([
        teamService.getTeams(),
        timesheetService.listWorkers({ active: true }),
        tasksService.getTasks({ projectId: effectiveProjectId, limit: 250 }),
        timesheetService.list({ projectId: effectiveProjectId, from: date, to: date }),
        attendanceService.list({ projectId: effectiveProjectId, attendanceDate: date }),
      ]);
      setTeams(teamRows.filter((team) => team.isActive !== false));
      setWorkers(workerRows.filter((worker) => worker.isActive !== false));
      setTasks(taskRows.tasks);
      setTimesheets(timesheetRows);
      setAttendance(attendanceRows);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load worker activity.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProjectId]);

  const teamById = useMemo(() => new Map(teams.map((team) => [team.id, team])), [teams]);

  const activityRows = useMemo<WorkerActivity[]>(() => {
    const attendanceByWorker = new Map(attendance.map((row) => [row.workerId, row]));
    return workers
      .map((worker) => {
        const workerTimesheets = timesheets.filter((sheet) =>
          sheet.entries?.some((entry) => entry.workerId === worker.id)
        );
        const taskIds = new Set(
          workerTimesheets.flatMap((sheet) =>
            sheet.entries?.filter((entry) => entry.workerId === worker.id && entry.taskId).map((entry) => entry.taskId as string) ?? []
          )
        );
        const directTasks = tasks.filter((task) => taskIds.has(task.id));
        const attendanceRow = attendanceByWorker.get(worker.id);
        const status: WorkerStatus = attendanceRow
          ? attendanceRow.scanOutAt
            ? 'clocked_out'
            : 'clocked_in'
          : 'not_seen';
        const hoursToday = workerTimesheets.reduce((sum, sheet) =>
          sum + (sheet.entries ?? [])
            .filter((entry) => entry.workerId === worker.id)
            .reduce((entrySum, entry) => entrySum + Number(entry.hours || 0), 0), 0);
        return {
          worker,
          team: worker.teamId ? teamById.get(worker.teamId) : undefined,
          attendance: attendanceRow,
          timesheets: workerTimesheets,
          currentTasks: directTasks.slice(0, 4),
          status,
          hoursToday,
        };
      })
      .sort((a, b) => (a.team?.name ?? 'Unassigned').localeCompare(b.team?.name ?? 'Unassigned') || a.worker.name.localeCompare(b.worker.name));
  }, [attendance, teamById, tasks, timesheets, workers]);

  const grouped = useMemo(() => {
    const rows = new Map<string, WorkerActivity[]>();
    activityRows.forEach((activity) => {
      const key = activity.team?.id ?? 'unassigned';
      rows.set(key, [...(rows.get(key) ?? []), activity]);
    });
    return rows;
  }, [activityRows]);

  const crewSummaries = useMemo<CrewSummary[]>(() => {
    const rows: CrewSummary[] = teams.map((team) => ({
      id: team.id,
      team,
      workers: grouped.get(team.id) ?? [],
    }));
    const unassignedWorkers = grouped.get('unassigned') ?? [];
    if (unassignedWorkers.length) rows.push({ id: 'unassigned', team: null, workers: unassignedWorkers });
    return rows.sort((a, b) => {
      const aConstruction = isConstructionCrew(a.team);
      const bConstruction = isConstructionCrew(b.team);
      if (aConstruction !== bConstruction) return aConstruction ? -1 : 1;
      return (a.team?.name ?? 'Unassigned workers').localeCompare(b.team?.name ?? 'Unassigned workers');
    });
  }, [grouped, teams]);

  const selectedCrew = crewSummaries.find((crew) => crew.id === selectedCrewId) ?? null;
  const constructionCrews = crewSummaries.filter((crew) => isConstructionCrew(crew.team));
  const workerFolderRows = constructionCrews.length ? constructionCrews : crewSummaries;

  const openCrew = (crewId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'worker');
    nextParams.set('crew', crewId);
    setSearchParams(nextParams);
  };

  const showAllCrews = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', 'worker');
    nextParams.delete('crew');
    setSearchParams(nextParams);
  };

  if (!effectiveProjectId) {
    return projectsLoading
      ? <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      : <Alert severity="warning">No project is available for worker activity.</Alert>;
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>Workers</Typography>
          <Typography variant="body2" color="text.secondary">Live team roster, attendance, and current task activity.</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : !selectedCrew ? (
        <Stack spacing={1.25}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Worker Folders</Typography>
          <Grid container spacing={1.5}>
            {workerFolderRows.map((crew) => {
              const clockedIn = crew.workers.filter((row) => row.status === 'clocked_in').length;
              const activeTasks = crew.workers.reduce((sum, row) => sum + row.currentTasks.length, 0);
              return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={crew.id}>
                <PersonnelFolderCard
                  name={crew.team?.name ?? 'Unassigned workers'}
                  email=""
                  details={[
                    `${crew.workers.length} worker${crew.workers.length === 1 ? '' : 's'}`,
                    `${clockedIn} clocked in`,
                    `${activeTasks} active task${activeTasks === 1 ? '' : 's'}`,
                  ]}
                  onClick={() => openCrew(crew.id)}
                />
              </Grid>
            );
            })}
          </Grid>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Button startIcon={<ArrowBackIcon />} onClick={showAllCrews} sx={{ alignSelf: 'flex-start', fontWeight: 900 }}>
            All worker folders
          </Button>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                  {selectedCrew.team?.name ?? 'Unassigned workers'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCrew.workers.length} workers in this folder
                </Typography>
              </Box>
              {selectedCrew.team?.teamLeadId && <Chip size="small" label="Team lead assigned" />}
            </Stack>
          </Paper>
          {selectedCrew.workers.length ? (
            <Grid container spacing={1.5}>
              {selectedCrew.workers.map((activity) => {
                const workerLogin = loginForWorker(activity.worker);
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={activity.worker.id}>
                    <PersonnelFolderCard
                      name={activity.worker.name}
                      email={workerLogin.email}
                      details={[
                        activity.worker.trade || 'No trade set',
                        statusLabel[activity.status],
                        `${activity.hoursToday || 0}h today`,
                        `Login: ${workerLogin.email}`,
                        activity.currentTasks[0]?.title ?? 'No current task assigned',
                      ]}
                      onClick={() => navigate(`/field/workers/${activity.worker.id}`)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Alert severity="info">No workers are assigned to this folder.</Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}
